<template>
  <v-row>
    <v-col cols="10">
      <p class="float-left">Applied Filters</p>

      <v-chip v-for="chip in chips" :key="chip.key" class="ma-2">
        {{ chip.text }}
      </v-chip>
    </v-col>
    <v-col cols="2">
      <v-row>
        <v-col cols="3">
          <filter-btn :dialog="dialog" class="mr-0">
            <filter-modal :department-choices="departments"
                          :page-level="pageLevel"
                          :photo-filters="filter"
                          :retailer-choices="retailers"
                          :surface="surface"
                          @apply-filters="applyFilters($event)"
                          @dialog-clicked="dialog = false"
                          @modal-mounted="dialog = true"
            />
          </filter-btn>
        </v-col>
        <v-col cols="9">
          <v-btn class="mr-0" color="primary" small @click="resetFilters">
            Reset Filters
          </v-btn>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
/**
 * The filter bar, for every surface. [IT-707 P2.2]
 *
 * Replaces PhotoFilter / QCPhotoFilter / GroupQCFilter. All three were the same
 * bar — chip strip, filter button, reset — differing only in which criteria they
 * declared and which ones they bothered to chip. PhotoFilter applied thirteen
 * criteria and chipped five, so a retailer or country selection took effect with
 * nothing on screen saying so.
 *
 * The payload now carries exactly the criteria the surface can apply, so a view
 * cannot be handed a key its backend throws away, and `pageLevel` keeps a
 * view-owned criterion out of the payload entirely — GroupCountQC spreads this
 * object over the same state its own checkboxes are bound to, and emitting
 * `onlyNeedsReview` from here would overwrite the operator's toggle.
 *
 * `surface` is a prop rather than a build-time choice because BoxTagging drives
 * one bar across two surfaces off `reviewMode`.
 */
import axios from 'axios'
import FilterBtn from '../btns/FilterBtn.vue'
import FilterModal from './FilterModal.vue'
import {coerceValue, defaultsFor, fieldsFor} from './fields'
import {supportedCriteria} from './capabilities'

export default {
  name: 'FilterBar',
  components: {
    FilterModal,
    FilterBtn,
  },
  props: {
    surface: {
      type: String,
      required: true,
    },
    initialFilter: {
      type: Object,
      default: () => ({}),
    },
    /** Criteria the VIEW owns in its own state: not rendered, not emitted. */
    pageLevel: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      dialog: false,
      retailers: [],
      departments: [],
      filter: defaultsFor(this.surface, this.pageLevel),
    }
  },
  mounted() {
    this.get_retailer_list()
    this.get_department_list()
    this.seedFromInitial()
  },
  computed: {
    /** The criteria this bar carries: what the surface supports, less the view's. */
    criteria() {
      return supportedCriteria(this.surface).filter((key) => !this.pageLevel.includes(key))
    },
    /** One chip per criterion that has something to say, in form order. */
    chips() {
      const out = []
      fieldsFor(this.surface, this.pageLevel).forEach((field) => {
        const value = this.filter[field.key]
        if (field.chipItem) {
          (value || []).forEach((item, index) => {
            out.push({key: `${field.key}-${item.id != null ? item.id : index}`, text: field.chipItem(item)})
          })
          return
        }
        const text = field.chip ? field.chip(value, this.surface) : null
        if (text) out.push({key: field.key, text})
      })
      return out
    },
  },
  watch: {
    /**
     * Rebuild on a mode switch, keeping what still applies.
     *
     * A value left behind for a criterion the new surface ignores would ride
     * along in the payload and look applied while doing nothing — which is the
     * whole defect class this ticket exists to close.
     */
    surface() {
      const kept = this.filter
      this.filter = defaultsFor(this.surface, this.pageLevel)
      this.criteria.forEach((key) => {
        if (kept[key] !== undefined) this.filter[key] = kept[key]
      })
    },
  },
  methods: {
    seedFromInitial() {
      const incoming = this.initialFilter || {}
      this.criteria.forEach((key) => {
        if (incoming[key] !== undefined) this.filter[key] = coerceValue(key, incoming[key])
      })
    },
    async get_retailer_list() {
      return this.fetchChoices('/api/retailers', 'get_retailer_list', 'retailers')
    },
    async get_department_list() {
      return this.fetchChoices('/api/departments', 'get_department_list', 'departments')
    },
    /** Shared fetch; this was two near-identical blocks in each of three bars. */
    fetchChoices(url, loadingKey, target) {
      const toggle = () => this.$store.commit('UPDATE_STORE_ARRAY_DATA', {
        fields: [{keys: ['loading_list'], value: loadingKey, action: 'toggle'}],
      })
      toggle()
      return axios.get(url)
        .then((response) => {
          this[target] = response.data
          return response.data
        })
        .catch((e) => {
          console.log(e)
          throw e
        })
        .finally(toggle)
    },
    applyFilters(filterValues) {
      const values = filterValues || {}
      this.criteria.forEach((key) => {
        this.filter[key] = values[key] !== undefined
          ? coerceValue(key, values[key])
          : defaultsFor(this.surface, this.pageLevel)[key]
      })

      this.dialog = false
      this.emitFilters()
    },
    resetFilters() {
      this.filter = defaultsFor(this.surface, this.pageLevel)
      this.emitFilters()
    },
    /**
     * Emit a copy. The views assign or spread this straight into their own
     * state, and handing them the live object made the bar's internals
     * reachable from the page.
     */
    emitFilters() {
      this.$emit('apply-filters', {...this.filter})
    },
  },
}
</script>

<style>
</style>
